import {AppContextBase, GameConfigurationBase} from '../../GameEngine';
import {Logger} from 'neo-common';
import {IClientHub} from './IClientHub';
import {PeakTournaments} from './Plugins/PeakTournaments';
import {BasePlugin} from './Plugins/BasePlugin';
import {AutoplayData, BetData, GameDataLoaded, SpinReceived} from '../../Platforms/IPlatforms';

export class PlayerRetentionManager {

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private _plugins: Array<BasePlugin>;

    private _configuration: GameConfigurationBase;

    private _clientHub: IClientHub;

    private _openedDialogs: Array<string>;

    private _loadStarted: boolean;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor(clientHub: IClientHub) {
        let appContext = AppContextBase.getInstance();
        if (appContext == null) {
            this.log(`Missing app context`);
            return;
        }

        this._plugins = [];
        this._openedDialogs = [];
        this._configuration = appContext.gameConfiguration;
        this._clientHub = clientHub;
        this._loadStarted = false;
        this.createPlugins();
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    private createPlugins(): void {
        let data = this._configuration.PlayerRetentionPlugins;

        for (let i = 0; i < data.length; i++) {
            const pluginArray: Array<string> = data[i].split(';');
            if (pluginArray.length !== 2) {
                this.log(`Invalid plugin array data - ${pluginArray.toString()}`);
                continue;
            }

            const instanceName = pluginArray[0];
            const pluginUrl = pluginArray[1];
            const plugin = this.createPlugin(instanceName, pluginUrl);

            if (plugin != null) {
                this._plugins.push(plugin);
            }
        }
    }

    private createPlugin(instanceName: string, pluginUrl: string): BasePlugin {
        switch (instanceName) {
            case 'PeakTournaments':
                return new PeakTournaments(instanceName, pluginUrl, this.getGameClientHub(), this._configuration);
            default :
                return new BasePlugin(instanceName, pluginUrl, this.getGameClientHub(), this._configuration);
        }
    }

    //******************************************************************************************************************
    //  CLIENT HUB
    //******************************************************************************************************************

    private getGameClientHub(): IClientHub {
        return this._clientHub;
    }

    //******************************************************************************************************************
    //  PLUGINS INIT
    //******************************************************************************************************************

    public loadProgress(progress: number): void {
        if (this._loadStarted) {
            return;
        }

        this.log(`loadStarted`);
        this._loadStarted = true;

        this.log('Plugins loading');
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].init();
        }
    }

    public gameReady(): void {
        //not implemented, moved to loadStarted

        /*this.log(`gameReady`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].init();
        }*/
    }

    //******************************************************************************************************************
    //  CLIENT EVENTS
    //******************************************************************************************************************

    public error(code: string, message: string): void {
        this.log(`error - ${code} - ${message}`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].error(code, message);
        }
    }

    public gameStarted(data: GameDataLoaded): void {
        this.log(`gameStarted - ${JSON.stringify(data)}`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gameStarted(data);
        }
    }

    public roundInit(): void {
        this.log(`roundInit`);
        if (BasePlugin.AsyncRunning) {
            return;
        }

        const functions: Array<() => Promise<void>> = [];
        for (let i = 0; i < this._plugins.length; i++) {
            functions.push(this._plugins[i].roundInit.bind(this._plugins[i]));
        }

        BasePlugin.AsyncRunning = true;
        Promise.each(functions, (item, index) => {
            return item();
        }).finally(() => {
            BasePlugin.AsyncRunning = false;
            BasePlugin.runAsyncTasks();
        }).catch(err => {
            console.log(err);
        });
    }

    public roundStarted(): void {
        this.log(`roundStarted`);
        this._openedDialogs = [];
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].roundStarted();
        }
    }

    public spinStarted(): void {
        this.log(`spinStarted`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].spinStarted();
        }
    }

    public spinReceived(args?: SpinReceived): void {
        this.log(`spinReceived - ${args ? JSON.stringify(args) : 'none'}`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].spinReceived(args);
        }
    }

    public spinEnded(): void {
        this.log(`spinEnded`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].spinEnded();
        }
    }

    public roundEnded(): void {
        this.log(`roundEnded`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].roundEnded();
        }
    }

    public featureStarted(): void {
        this.log(`featureStarted`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].featureStarted();
        }
    }

    public featureInit(): void {
        this.log(`featureInit`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].featureInit();
        }
    }

    public featureFinished(): void {
        this.log(`featureFinished`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].featureFinished();
        }
    }

    public autoplayStarted(): void {
        this.log(`autoplayStarted`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].autoplayStarted();
        }
    }

    public autoplayFinished(args?: AutoplayData): void {
        this.log(`autoplayFinished - ${args ? JSON.stringify(args) : 'none'}`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].autoplayFinished(args);
        }
    }

    public gotoHelp(): void {
        this.log(`gotoHelp`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gotoHelp();
        }
    }

    public gotoPaytable(): void {
        this.log(`gotoPaytable`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gotoPaytable();
        }
    }

    public gotoSettings(): void {
        this.log(`gotoSettings`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gotoSettings();
        }
    }

    public setVolume(level: number): void {
        this.log(`setVolume - ${level}`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].setVolume(level);
        }
    }

    public betChanged(bet: BetData): void {
        this.log(`betChanged - ${bet ? JSON.stringify(bet) : 'none'}`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].betChanged(bet);
        }
    }

    public dialogOpened(type: string, isOpen: boolean): void {
        this.log(`dialogOpened - type: ${type} - isOpen: ${isOpen}`);
        if (isOpen) {
            //frb is able to send open multiple times, even for the same dialog
            if (type === 'frb' && this._openedDialogs.indexOf(type) > -1) {
                return;
            }

            this._openedDialogs.push(type);
        } else {
            const itemIndex = this._openedDialogs.indexOf(type);
            if (itemIndex === -1) {
                //trying to close dialog that is already closed
                return;
            }

            this._openedDialogs.splice(itemIndex, 1);

            if (this._openedDialogs.length > 0) {
                //wait until all dialogs are closed
                return;
            }
        }


        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].dialogOpened(type, isOpen);
        }
    }

    public closeMenu(): void {
        this.log(`closeMenu`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].closeMenu();
        }
    }

    public gamePlayStop(): void {
        this.log(`gamePlayStop`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gamePlayStop();
        }
    }

    public gamePlayResume(): void {
        this.log(`gamePlayResume`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gamePlayResume();
        }
    }

    public gamePlayFreeze(): void {
        this.log(`gamePlayFreeze`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gamePlayFreeze();
        }
    }

    public gamePlayUnfreeze(): void {
        this.log(`gamePlayUnfreeze`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].gamePlayUnfreeze();
        }
    }

    public webSocketsInitialized(): void {
        this.log(`webSocketsInitialized`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].webSocketsInitialized();
        }
    }

    public iconsCountAvailable(count: number): void {
        this.log(`pluginIconSlots - ${count}`);
        for (let i = 0; i < this._plugins.length; i++) {
            this._plugins[i].iconsCountAvailable(count);
        }
    }

    //******************************************************************************************************************
    //  LOGGER
    //******************************************************************************************************************

    protected log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup('PlayerRetentionManager', 'PlayerRetentionManager ' + s);
        }
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Plugins/PlayerRetention/PlayerRetentionManager.ts
 **/